import React from 'react';
import { Page, Document, StyleSheet, Text, View } from '@react-pdf/renderer';
import PdfHeader from '../components/reusable/PdfHeader';
import PageNumber from '../components/reusable/PageNumber';
import ChemAppsAgreementsSection from './sections/ChemAppsAgreementsSection';

const styles = StyleSheet.create({
    body: {
        padding: 10,
        paddingBottom: 5, // For page number
    },
    instructionsPage: {
        padding: 20,
        paddingBottom: 30,
        backgroundColor: '#ffffff',
    },
    instructionsHeader: {
        textAlign: 'center',
        marginBottom: 10,
        padding: 8,
        backgroundColor: '#f0f7ff',
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#2e7d32',
        borderRadius: 4,
    },
    instructionsTitle: {
        fontSize: 14,
        fontFamily: 'Helvetica-Bold',
        color: '#2e7d32',
    },
    instructionsMeta: {
        fontSize: 9,
        color: '#6c757d',
        textAlign: 'center',
        marginTop: 2,
    },
    instructionsBox: {
        marginTop: 8,
        padding: 12,
        borderWidth: 1,
        borderStyle: 'solid',
        borderColor: '#dee2e6',
        borderRadius: 4,
        backgroundColor: '#fcfcfd',
    },
    instructionsText: {
        fontSize: 10,
        lineHeight: 1.4,
        color: '#343a40',
    },
});

interface ChemAppsAgreementsPdfProps {
    data: {
        agreement: any;
        sheetType: 'chemApps' | 'disinfectant' | 'sprayBar';
        instructions?: string;
    };
    customerSignature?: any; // From imageStore
}

const ChemAppsAgreementsPdf: React.FC<ChemAppsAgreementsPdfProps> = ({ data, customerSignature }) => {
    // Create header data for the PDF
    const headerData = {
        ...data.agreement,
        sheetType: data.sheetType,
        isAgreement: true
    };

    // Determine the agreement title based on sheet type
    const getAgreementTitle = (sheetType: string): string => {
        switch (sheetType) {
            case 'chemApps':
                return 'Custom Application Agreement';
            case 'sprayBar':
                return 'Spray Bar Agreement';
            case 'disinfectant':
                return 'Disinfectant Agreement';
            default:
                return 'Service Agreement';
        }
    };

    return (
        <Document>
            <Page size="A4" style={styles.body}>
                <PdfHeader company={data.agreement?.company || ''} sheet={headerData} />
                <ChemAppsAgreementsSection
                    agreement={data.agreement}
                    sheetType={data.sheetType}
                    agreementTitle={getAgreementTitle(data.sheetType)}
                    customerSignature={customerSignature}
                />
                <PageNumber stage={data.agreement?.stage} sheetType={getAgreementTitle(data.sheetType)} />
            </Page>
            {!!(data.instructions && `${data.instructions}`.trim()) && (
                <Page size="A4" style={styles.instructionsPage}>
                    <PdfHeader company={data.agreement?.company || ''} sheet={headerData} />
                    <View style={styles.instructionsHeader}>
                        <Text style={styles.instructionsTitle}>Special Instructions</Text>
                        <Text style={styles.instructionsMeta}>
                            {data?.agreement?.customerName ? `Customer: ${data.agreement.customerName}` : ''}
                        </Text>
                    </View>
                    <View style={styles.instructionsBox}>
                        <Text style={styles.instructionsText}>
                            {data.instructions}
                        </Text>
                    </View>
                    <PageNumber stage={data.agreement?.stage} sheetType={getAgreementTitle(data.sheetType)} />
                </Page>
            )}
        </Document>
    );
};

export default ChemAppsAgreementsPdf;
